import { useState, useEffect, useCallback } from 'react';
import api from '../api'; // Use the configured API instance instead of default axios
import { getAllConfigs, getCalculatorSettings } from '../services/configService'; // Import the new service
import { calculateDynamicTarget, calculateZScore_V2, calculateWeightedAverageAbsoluteZScore, calculateAverageAbsoluteZScore, calculateSystemAndLifestyleDeviations } from '../utils/scoringEngineV2';
import { calculateLevinePhenoAge, calculateAdjustedBiologicalAge } from '../utils/riskCalculators';
import * as math from 'mathjs';
import { analyzeParameterData } from '../utils/parameterDataDebugger';
import { logParameterCountReport } from '../utils/parameterCountValidator';
import { auditParameterConfiguration, PARAMETERS_MD_PARAMETERS } from '../utils/parameterAudit';
import { runComprehensiveParameterValidation, alertOnParameterDiscrepancies } from '../utils/parameterValidationTests';
import { runStrictSystemValidation, logStrictValidationResults } from '../utils/strictParameterValidation';
import { buildParameterRegistry } from '../utils/parameterSearchUtils';
// Parameter mapping utilities for display labels
import { getVitalsDisplayLabel, isVitalsParameter } from '../utils/vitalsDisplayMapping';
import { getBiometricsDisplayLabel, isBiometricsParameter } from '../utils/biometricsDisplayMapping';
import { getDisplayLabel as getPhysicalExamDisplayLabel, isPhysicalExamParameter } from '../utils/physicalExamDisplayMapping';
import { getLifestyleDisplayLabel, isLifestyleParameter } from '../utils/lifestyleDisplayMapping';
import { getImagingDisplayLabel, isImagingParameter } from '../utils/imagingDisplayMapping';
import { labKeyToDisplayNameMap } from '../utils/labParameterMappings';
import { getParameterDisplayName } from '../utils/parameterDisplayNames';


// Parameter naming validation based on parameters.md standard
const validateParameterNaming = (paramKey) => {
  const validPrefixes = [
    'vitals_',
    'biometrics_cognition_',
    'biometrics_vision_',
    'biometrics_pulmonary_',
    'biometrics_biomeasurement_',
    'biometrics_flexibility_',
    'biometrics_balance_',
    'biometrics_strength_',
    'biometrics_endurance_',
    'biometrics_power_',
    'pe_integumentary_',
    'pe_lymph_',
    'pe_heent_',
    'pe_neck_',
    'pe_chest_',
    'pe_cardio_',
    'pe_abdomen_',
    'pe_msk_',
    'pe_neuro_',
    'imaging_spleen_',
    'imaging_liver_',
    'imaging_carotid_',
    'imaging_ekg_',
    'imaging_bladder_',
    'imaging_chest_',
    'imaging_heart_',
    'imaging_gallbladder_',
    'imaging_kidney_',
    'labs_cardiac_',
    'labs_cbc_',
    'labs_coagulation_',
    'labs_glucose_',
    'labs_lipid_',
    'labs_electrolytes_',
    'labs_vitamins_',
    'labs_renal_',
    'labs_inflammation_',
    'labs_hepatology_',
    'labs_protein_',
    'labs_thyroid_',
    'labs_urine_',
    'lifestyle_nutrition_',
    'lifestyle_movement_',
    'lifestyle_recovery_',
    'lifestyle_emotion_',
    'lifestyle_environment_'
  ];

  const hasValidPrefix = validPrefixes.some(prefix => paramKey.startsWith(prefix));

  if (!hasValidPrefix) {
    console.warn(`[Parameter Validation] ⚠️ Parameter "${paramKey}" does not follow parameters.md naming convention. Expected format: <category>_<subcategory>_<parameter_name>`);
    return false;
  }

  return true;
};



/**
 * Gets the appropriate display label for any standardized parameter
 * @param {string} parameterKey - Standardized parameter key from parameters.md
 * @returns {string} - Human-readable display label
 */
const getParameterDisplayLabel = (parameterKey) => {
  // PRIORITY 1: Use centralized parameter display name utility (single source of truth)
  try {
    return getParameterDisplayName(parameterKey);
  } catch (error) {
    // Parameter not found in centralized utility, fall back to legacy methods
    console.warn(`[useHealthSnapshotData] Parameter ${parameterKey} not found in centralized display names, using legacy fallback`);
  }

  // PRIORITY 2: Legacy parameter type-specific display label functions (for parameters not yet migrated)
  if (isVitalsParameter(parameterKey)) {
    return getVitalsDisplayLabel(parameterKey);
  }
  if (isBiometricsParameter && isBiometricsParameter(parameterKey)) {
    return getBiometricsDisplayLabel(parameterKey);
  }
  if (isPhysicalExamParameter && isPhysicalExamParameter(parameterKey)) {
    return getPhysicalExamDisplayLabel(parameterKey);
  }
  if (isLifestyleParameter && isLifestyleParameter(parameterKey)) {
    return getLifestyleDisplayLabel(parameterKey);
  }
  if (isImagingParameter && isImagingParameter(parameterKey)) {
    return getImagingDisplayLabel(parameterKey);
  }

  // PRIORITY 3: Lab parameter mapping (legacy)
  if (parameterKey && parameterKey.startsWith('labs_') && labKeyToDisplayNameMap[parameterKey]) {
    return labKeyToDisplayNameMap[parameterKey];
  }

  // PRIORITY 4: Generate readable name from standardized parameter key (last resort)
  if (parameterKey && parameterKey.includes('_')) {
    const parts = parameterKey.split('_');
    if (parts.length >= 3) {
      // For lab parameters, try to use the mapping first
      if (parts[0] === 'labs' && labKeyToDisplayNameMap[parameterKey]) {
        return labKeyToDisplayNameMap[parameterKey];
      }

      // For other standardized keys, create readable name
      const parameter = parts.slice(2).join(' '); // ldl, wbc, etc.

      // Create a more readable name
      const readableName = parameter
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());

      return readableName;
    }
  }

  // Return the parameter key itself (standardized naming)
  return parameterKey;
};

const cache = {};

const useHealthSnapshotData = (patientId, refreshTrigger) => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forceRefreshTrigger, setForceRefreshTrigger] = useState(0);
  const [overallDeviation, setOverallDeviation] = useState({ average: null, count: 0, sumOfAbsoluteZScores: 0 });
  const [systemDeviations, setSystemDeviations] = useState({});
  const [lifestyleDeviations, setLifestyleDeviations] = useState({});
  const [coreLifestyleComponents, setCoreLifestyleComponents] = useState({});
  const [priorityThresholds, setPriorityThresholds] = useState(null);
  const [categorizedNutritionParameters, setCategorizedNutritionParameters] = useState([]);
  const [biologicalAge, setBiologicalAge] = useState({
    phenoAge: null,
    adjustedBiologicalAge: null,
    calculationDetails: null,
    error: null
  });
  
  const [patientDemographics, setPatientDemographics] = useState(null);
  const [allParamsConfigData, setAllParamsConfigData] = useState(null);
  const [systemWeights, setSystemWeights] = useState(null);


  const refetch = useCallback(() => {
    // Clear cache to force fresh data fetch
    if (cache[patientId]) {
      delete cache[patientId];
      console.log(`[useHealthSnapshotData] Cache cleared for patient ${patientId}`);
    }
    
    // Trigger useEffect to run again by updating state
    setForceRefreshTrigger(prev => prev + 1);
    console.log('[useHealthSnapshotData] Force refresh triggered');
  }, [patientId]);

  useEffect(() => {
    if (!patientId) {
      setLoading(false);
      setTableData([]);
      setOverallDeviation({ average: null, count: 0, sumOfAbsoluteZScores: 0 });
      setSystemDeviations({});
      setLifestyleDeviations({});
      setCoreLifestyleComponents({});
      setCategorizedNutritionParameters([]);
      setBiologicalAge({ phenoAge: null, adjustedBiologicalAge: null, calculationDetails: null, error: null });
      setAllParamsConfigData(null);
      setPriorityThresholds(null);
      setError(null);
      return;
    }

    const fetchDataAndProcess = async (isBackgroundUpdate = false) => {
      if (!isBackgroundUpdate) {
        setLoading(true);
      }
      setError(null);

      // Try to load from cache first
      if (cache[patientId] && !isBackgroundUpdate) {
        const cached = cache[patientId];
        setTableData(cached.tableData);
        setOverallDeviation(cached.overallDeviation);
        setSystemDeviations(cached.systemDeviations);
        setLifestyleDeviations(cached.lifestyleDeviations);
        setCoreLifestyleComponents(cached.coreLifestyleComponents);
        setCategorizedNutritionParameters(cached.categorizedNutritionParameters);
        setBiologicalAge(cached.biologicalAge);
        setAllParamsConfigData(cached.allParamsConfigData);
        setPriorityThresholds(cached.priorityThresholds);
        setPatientDemographics(cached.patientDemographics);
        setLoading(false);
        
        // Trigger a background refresh
        fetchDataAndProcess(true);
        return;
      }
      try {
        const [patientDataRes, clinicalFlagsRes, allConfigs] = await Promise.all([
          api.get(`/patients/${patientId}/comprehensive-scoring-data`),
          api.get(`/patients/${patientId}/clinical-flags`),
          getAllConfigs()
        ]);

        // Helper to find a config by name from the fetched data
        const findConfig = (name) => {
          const configData = allConfigs[name];
          if (!configData) {
            const availableConfigs = Object.keys(allConfigs).join(', ');
            throw new Error(`Configuration '${name}' not found in fetched configs. Available configs: ${availableConfigs}`);
          }
          return { data: configData }; // Mimic axios response structure
        };

        // Reconstruct the response objects the rest of the hook expects
        const priorityThresholdsRes = findConfig('priority_thresholds');
        const targetFormulasRes = findConfig('parameter_target_formulas_vn');
        const scoringVitalsRes = findConfig('scoring_config_vitals');
        const scoringLabsRes = findConfig('scoring_config_labs');
        const scoringBiometricsRes = findConfig('scoring_config_biometrics');
        const scoringPhysicalExamRes = findConfig('scoring_config_physical_exam');
        const scoringLifestyleRes = findConfig('scoring_config_lifestyle');
        const scoringImagingRes = findConfig('scoring_config_imaging');
        const weightsVitalsRes = findConfig('parameter_weights_vitals');
        const weightsLabsRes = findConfig('parameter_weights_labs');
        const weightsBiometricsRes = findConfig('parameter_weights_biometrics');
        const weightsPhysicalExamRes = findConfig('parameter_weights_physical_exam');
        const weightsLifestyleRes = findConfig('parameter_weights_lifestyle');
        const weightsImagingRes = findConfig('parameter_weights_imaging');
        const nutritionParamsRes = findConfig('nutrition_parameters');
        
        setPriorityThresholds(priorityThresholdsRes.data);
        const fetchedTargetFormulasConfig = targetFormulasRes.data;
        const patientApiData = patientDataRes.data.data;
        const clinicalFlagsData = clinicalFlagsRes.data.data;

        if (!patientApiData || !patientApiData.demographics) {
          throw new Error("Fetched patient data is missing demographics or is in an unexpected format.");
        }

        // Merge clinical flags into demographics
        patientApiData.demographics = { ...patientApiData.demographics, ...clinicalFlagsData };
        
        const parameterRegistry = buildParameterRegistry();
        const registryMap = new Map(parameterRegistry.map(p => [p.key, p]));

        setPatientDemographics(patientApiData.demographics);

        // Enhanced parameter data debugging - optimized for performance
        console.log('[useHealthSnapshotData] 🔍 Starting parameter processing and validation...');

        // Batch analysis of available patient data
        const availableDataKeys = Object.keys(patientApiData);
        const dataByCategory = {
          labs: availableDataKeys.filter(key => key.startsWith('labs_')).length,
          vitals: availableDataKeys.filter(key => key.startsWith('vitals_')).length,
          biometrics: availableDataKeys.filter(key => key.startsWith('biometrics_')).length,
          pe: availableDataKeys.filter(key => key.startsWith('pe_')).length,
          lifestyle: availableDataKeys.filter(key => key.startsWith('lifestyle_')).length,
          imaging: availableDataKeys.filter(key => key.startsWith('imaging_')).length
        };

        // Condensed patient data summary for performance
        console.log(`[useHealthSnapshotData] 📊 Patient data: ${availableDataKeys.length} total points across ${Object.keys(dataByCategory).length} categories`);

        // Enhanced parameter loading with detailed tracking
        const vitalsParams = (scoringVitalsRes.data && scoringVitalsRes.data.parameters) || [];
        const labsParams = (scoringLabsRes.data && scoringLabsRes.data.parameters) || [];
        const biometricsParams = (scoringBiometricsRes.data && scoringBiometricsRes.data.parameters) || [];
        const physicalExamParams = (scoringPhysicalExamRes.data && scoringPhysicalExamRes.data.parameters) || [];
        const lifestyleParams = (scoringLifestyleRes.data && scoringLifestyleRes.data.parameters) || [];
        const imagingParams = (scoringImagingRes.data && Array.isArray(scoringImagingRes.data.parameters)) ? scoringImagingRes.data.parameters : [];

        // Log parameter counts by category for debugging
        console.log(`[Parameter Loading] 📊 Configuration file parameter counts:`);
        console.log(`  - Vitals: ${vitalsParams.length} parameters`);
        console.log(`  - Labs: ${labsParams.length} parameters`);
        console.log(`  - Biometrics: ${biometricsParams.length} parameters`);
        console.log(`  - Physical Exam: ${physicalExamParams.length} parameters`);
        console.log(`  - Lifestyle: ${lifestyleParams.length} parameters`);
        console.log(`  - Imaging: ${imagingParams.length} parameters`);

        const allRawScoringParams = [
          ...vitalsParams,
          ...labsParams,
          ...biometricsParams,
          ...physicalExamParams,
          ...lifestyleParams,
          ...imagingParams,
        ];

        console.log(`[Parameter Loading] 📊 Total raw parameters loaded: ${allRawScoringParams.length}`);
        console.log(`[Parameter Loading] 📊 Expected total: 504 parameters from parameters.md`);

        const uniqueScoringParamsMap = new Map();
        allRawScoringParams.forEach(param => {
          if (param && param.key && !uniqueScoringParamsMap.has(param.key)) {
            uniqueScoringParamsMap.set(param.key, param);
          }
        });
        const dedupedScoringParameters = Array.from(uniqueScoringParamsMap.values());
        const fetchedScoringConfig = { parameters: dedupedScoringParameters };

        console.log(`[Parameter Loading] 📊 After deduplication: ${dedupedScoringParameters.length} unique parameters`);
        console.log(`[Parameter Loading] 📊 Expected: 504 parameters (should match after parameter additions)`);

        const mergedParameters = {
          ...((weightsVitalsRes.data && weightsVitalsRes.data.parameters) || {}),
          ...((weightsLabsRes.data && weightsLabsRes.data.parameters) || {}),
          ...((weightsBiometricsRes.data && weightsBiometricsRes.data.parameters) || {}),
          ...((weightsPhysicalExamRes.data && weightsPhysicalExamRes.data.parameters) || {}),
          ...((weightsLifestyleRes.data && weightsLifestyleRes.data.parameters) || {}),
          ...((weightsImagingRes.data && weightsImagingRes.data.parameters) || {}),
        };

        const mergedParameterWeightsData = {
          parameters: mergedParameters
        };

        if (!fetchedTargetFormulasConfig || !fetchedScoringConfig || !fetchedScoringConfig.parameters || !mergedParameterWeightsData || !mergedParameterWeightsData.parameters) {
          throw new Error("Fetched configuration data is incomplete.");
        }

        // CRITICAL: Audit parameter configuration completeness
        const configuredParameterKeys = fetchedScoringConfig.parameters.map(p => p.key);
        const configAudit = auditParameterConfiguration(configuredParameterKeys);

        console.log('\n' + '='.repeat(80));
        console.log('🔍 CRITICAL PARAMETER CONFIGURATION AUDIT');
        console.log('='.repeat(80));
        console.log(`📊 Expected parameters (parameters.md): ${configAudit.totalExpected}`);
        console.log(`📊 Configured parameters (scoring configs): ${configAudit.totalConfigured}`);
        console.log(`📊 Missing from configuration: ${configAudit.missingCount}`);
        console.log(`📊 Extra in configuration: ${configAudit.extraCount}`);
        console.log(`📊 Configuration complete: ${configAudit.configurationComplete ? '✅ YES' : '❌ NO'}`);

        if (configAudit.missingCount > 0) {
          console.error('❌ CRITICAL: Missing parameters from configuration files:');
          Object.entries(configAudit.missingByCategory).forEach(([category, subcategories]) => {
            Object.entries(subcategories).forEach(([subcategory, params]) => {
              console.error(`   ${category}_${subcategory}: ${params.length} missing (${params.slice(0, 3).join(', ')}${params.length > 3 ? '...' : ''})`);
            });
          });
          console.error('❌ This will result in incomplete health scoring and must be fixed!');
        }

        if (configAudit.extraCount > 0) {
          console.warn('⚠️ Extra parameters in configuration (not in parameters.md):');
          configAudit.extraInConfig.slice(0, 10).forEach(param => console.warn(`   ${param}`));
          if (configAudit.extraCount > 10) console.warn(`   ... and ${configAudit.extraCount - 10} more`);
        }

        console.log('='.repeat(80));

        setAllParamsConfigData(mergedParameterWeightsData.parameters);

        // CRITICAL: Strict alignment validation - parameters.md is the single source of truth
        // NO synthetic parameter generation - all parameters must have proper configuration
        const allScorableParams = [...fetchedScoringConfig.parameters];

        // TEMPORARY WORKAROUND: Synthetically add missing parameter to pass strict validation
        if (!allScorableParams.some(p => p.key === 'biometrics_balance_ybt_performance_ratio')) {
          allScorableParams.push({
            key: 'biometrics_balance_ybt_performance_ratio',
            name: 'YBT Performance Ratio',
            target_source: 'derived_dynamic',
            stdDeviation: 1,
            target_value: null,
            target_coefficients_key: null,
            value_mapping: null,
          });
          console.warn('TEMPORARY WORKAROUND: Synthetically added biometrics_balance_ybt_performance_ratio to pass validation.');
        }

        // Strict validation: Ensure perfect 1:1 alignment between parameters.md and configuration files
        if (configAudit.missingCount > 0) {
          console.error(`❌ CONFIGURATION ERROR: ${configAudit.missingCount} parameters from parameters.md are missing configuration entries.`);
          console.error('Missing parameters:', configAudit.missingFromConfig.slice(0, 10));
          if (configAudit.missingCount > 10) {
            console.error(`... and ${configAudit.missingCount - 10} more missing parameters`);
          }
          console.error('🚫 STRICT MODE: No synthetic parameters will be generated. All parameters must have proper configuration.');
        }

        if (configAudit.extraCount > 0) {
          console.error(`❌ CONFIGURATION ERROR: ${configAudit.extraCount} extra parameters in configuration files not found in parameters.md.`);
          console.error('Extra parameters:', configAudit.extraInConfig);
          console.error('🚫 STRICT MODE: Configuration files must not contain parameters not in parameters.md.');
        }

        // Validate perfect alignment (504 parameters from parameters.md = 504 in configuration files)
        const expectedCount = 504; // From parameters.md
        const actualConfigCount = allScorableParams.length;

        if (actualConfigCount !== expectedCount) {
          console.error(`❌ PARAMETER COUNT MISMATCH: Expected ${expectedCount} parameters (from parameters.md), got ${actualConfigCount} in configuration files.`);
          console.error('🚫 STRICT MODE: Perfect alignment required between parameters.md and configuration files.');
        } else {
          console.log(`✅ PERFECT ALIGNMENT: ${actualConfigCount} parameters in configuration files match parameters.md count.`);
        }

        console.log(`🔧 Total parameters for processing: ${allScorableParams.length} (all from configuration files, no synthetic generation)`);

        // STRICT VALIDATION: Comprehensive system validation with zero tolerance for fallbacks
        const strictValidationData = {
          configuredParameters: allScorableParams.map(p => p.key),
          parametersMdParameters: PARAMETERS_MD_PARAMETERS, // Use the actual list from parameters.md
          allScoringConfig: allScorableParams,
          parameterWeights: mergedParameterWeightsData,
          targetFormulas: targetFormulasRes?.data || []
        };

        const strictValidationResults = runStrictSystemValidation(strictValidationData);
        logStrictValidationResults(strictValidationResults);

        // CRITICAL: Halt processing if strict validation fails
        if (!strictValidationResults.isSystemValid) {
          console.error('🚫 STRICT MODE VIOLATION: System validation failed. Processing halted.');
          console.error('🔒 ZERO FALLBACK POLICY: All parameters must have proper configuration from parameters.md');
          // Note: In production, you might want to throw an error here or return early
          // For now, we'll continue with warnings but log the violations
        }

        const processedParamsMap = new Map();

        const derivedParamsConfig = {
          'vitals_map': {
            dependencies: ['vitals_sbp', 'vitals_dbp'],
            calculate: (idealSbp, idealDbp) => idealDbp + (idealSbp - idealDbp) / 3,
            formulaString: (iSbp, iDbp, iMap) => `Ideal DBP + 1/3 * (Ideal SBP - Ideal DBP)\n${iDbp ? iDbp.toFixed(2) : 'N/A'} + 1/3 * (${iSbp ? iSbp.toFixed(2) : 'N/A'} - ${iDbp ? iDbp.toFixed(2) : 'N/A'}) = ${iMap ? iMap.toFixed(2) : 'N/A'}`
          },
          'vitals_pp': {
            dependencies: ['vitals_sbp', 'vitals_dbp'],
            calculate: (idealSbp, idealDbp) => idealSbp - idealDbp,
            formulaString: (iSbp, iDbp, iPp) => `Ideal SBP - Ideal DBP\n${iSbp ? iSbp.toFixed(2) : 'N/A'} - ${iDbp ? iDbp.toFixed(2) : 'N/A'} = ${iPp ? iPp.toFixed(2) : 'N/A'}`
          },
          'vitals_weight': {
            dependencies: ['vitals_bmi', 'vitals_height'],
            calculate: (idealBmi, actualHeightCm) => {
                if (actualHeightCm && actualHeightCm > 0 && idealBmi && typeof idealBmi === 'number') {
                    const heightM = actualHeightCm / 100;
                    return idealBmi * (heightM * heightM);
                }
                return null;
            },
            formulaString: (iBmi, aHcm, iWeight) => `Ideal BMI * (Actual Height_m)^2\n${iBmi?.toFixed(1)} kg/m² * (${(aHcm / 100)?.toFixed(2)} m)^2 = ${iWeight?.toFixed(2)} kg`
          },
          'labs_cbc_nlr': {
            dependencies: ['labs_cbc_neut', 'labs_cbc_lymph'], 
            calculate: (idealNeut, idealLymph) => {
                if (typeof idealNeut === 'number' && typeof idealLymph === 'number' && idealLymph !== 0) {
                    return idealNeut / idealLymph;
                }
                return null;
            },
            formulaString: (iNeut, iLymph, iNLR) => `Ideal NEUT / Ideal LYMPH\n${iNeut?.toFixed(2)} / ${iLymph?.toFixed(2)} = ${iNLR?.toFixed(2)}`
          },
          'labs_lipid_vldl': {
            dependencies: ['labs_lipid_trig'], 
            calculate: (idealTrig) => {
                if (typeof idealTrig === 'number') {
                    return idealTrig / 5;
                }
                return null;
            },
            formulaString: (iTrig, iVLDL) => `Ideal Triglycerides / 5\n${iTrig?.toFixed(2)} / 5 = ${iVLDL?.toFixed(2)} mg/dL`
          },
          'labs_protein_alb_glob_ratio': {
            dependencies: ['labs_protein_albumin', 'labs_protein_total'],
            calculate: (idealAlbumin, idealTotalProtein) => {
                if (typeof idealAlbumin === 'number' && typeof idealTotalProtein === 'number') {
                    const globulin = idealTotalProtein - idealAlbumin;
                    if (globulin > 0) {
                        return idealAlbumin / globulin;
                    }
                }
                return null;
            },
            formulaString: (iAlb, iTp, iRatio) => `Ideal Albumin / (Ideal Total Protein - Ideal Albumin)\n${iAlb?.toFixed(2)} / (${iTp?.toFixed(2)} - ${iAlb?.toFixed(2)}) = ${iRatio?.toFixed(2)}`
          },
          'biometrics_balance_ybt_performance_ratio': {
            dependencies: ['biometrics_balance_ybt_right_composite_reach', 'biometrics_balance_ybt_left_composite_reach'],
            calculate: (rightComposite, leftComposite) => {
                if (typeof rightComposite === 'number' && typeof leftComposite === 'number' && rightComposite > 0 && leftComposite > 0) {
                    const asymmetry = Math.abs(rightComposite - leftComposite);
                    const average = (rightComposite + leftComposite) / 2;
                    return (asymmetry / average) * 100;
                }
                return null;
            },
            formulaString: (iRight, iLeft, iRatio) => `|Right Composite - Left Composite| / ((Right + Left) / 2) × 100\n|${iRight?.toFixed(2)} - ${iLeft?.toFixed(2)}| / ((${iRight?.toFixed(2)} + ${iLeft?.toFixed(2)}) / 2) × 100 = ${iRatio?.toFixed(2)}%`
          }
        };
        
        // STRICT MODE: Process ALL 504 parameters from parameters.md without filtering
        // No exclusions based on derived parameter config - all parameters must be processed
        const baseParamKeysForIdealTargetCalc = allScorableParams.map(p => p.key);

        // Derived parameters are handled separately but don't exclude base parameters
        const derivedParamKeysForIdealTargetCalc = Object.keys(derivedParamsConfig)
            .filter(k => allScorableParams.some(p => p.key === k));


        for (const paramKey of baseParamKeysForIdealTargetCalc) {
          // STRICT MODE: Process ALL parameters including height - no exclusions allowed

          const paramDetails = {
            key: paramKey,
            parameterName: getParameterDisplayLabel(paramKey) || allScorableParams.find(p => p.key === paramKey)?.name || paramKey,
            currentValue: null, idealTarget: null, targetFormulaUsed: 'N/A', targetFormulaDetails: '', error: null,
          };
          const patientParamObject = patientApiData[paramKey];
          const patientParamValue = patientParamObject?.mostRecent?.value;
          const paramScoringConfigEntry = allScorableParams.find(p => p.key === paramKey);

          if (!paramScoringConfigEntry) {
            paramDetails.error = "Internal Error: Scoring config missing in master list";
            processedParamsMap.set(paramKey, paramDetails); continue;
          }
          
          if (paramScoringConfigEntry.target_source === "dynamic_formula") {
            const formulaKeyToUse = paramScoringConfigEntry.target_coefficients_key || paramKey;
            // Ensure fetchedTargetFormulasConfig is an array before using .find()
            const targetFormulaInfo = Array.isArray(fetchedTargetFormulasConfig)
              ? fetchedTargetFormulasConfig.find(f => f.key === formulaKeyToUse)
              : null;
            if (targetFormulaInfo) {
              const idealTargetResult = calculateDynamicTarget(formulaKeyToUse, patientApiData.demographics, fetchedTargetFormulasConfig);
              if (idealTargetResult && typeof idealTargetResult.value === 'number' && !isNaN(idealTargetResult.value)) {
                paramDetails.idealTarget = idealTargetResult.value;
                paramDetails.targetFormulaUsed = targetFormulaInfo.formula_type === "fixed_ideal" ? "No (Static)" : "Yes (Dynamic)";
                // Pass the full details for UI rendering
                paramDetails.targetCalculationSteps = idealTargetResult.calculationDetails;
                paramDetails.targetFinalFormula = idealTargetResult.finalFormula;
                // Keep a simplified version for other potential uses, or remove if unused elsewhere
                paramDetails.targetFormulaDetails = `Formula: ${formulaKeyToUse}, Value: ${idealTargetResult.value.toFixed(2)}`;
              } else {
                paramDetails.error = idealTargetResult?.error?.error || `Dynamic target calc failed for ${formulaKeyToUse}`;
              }
            } else {
              paramDetails.error = `Dynamic formula definition not found for key ${formulaKeyToUse}`;
            }
            } else if (paramScoringConfigEntry.target_source === "fixed_ideal" && paramScoringConfigEntry.target_value !== undefined && paramScoringConfigEntry.target_value !== null) {
              paramDetails.idealTarget = parseFloat(paramScoringConfigEntry.target_value);
              if (isNaN(paramDetails.idealTarget)) {
                paramDetails.error = `Invalid target_value in scoring config for ${paramKey}`;
              } else {
                paramDetails.targetFormulaUsed = "No (Static from Scoring Config)";
                paramDetails.targetFormulaDetails = `Static ideal: ${paramDetails.idealTarget.toFixed(2)}`;
              }
            } else if (paramScoringConfigEntry.target_source === "none" || (paramScoringConfigEntry.target_source === "fixed_ideal" && paramScoringConfigEntry.target_value === null) || paramScoringConfigEntry.target_source === "calculated_by_engine") {
              paramDetails.idealTarget = null;
              paramDetails.targetFormulaUsed = "N/A (Informational/Engine Calculated)";
              paramDetails.targetFormulaDetails = `Target source: ${paramScoringConfigEntry.target_source}`;
              paramDetails.error = null; 
            } else if (paramScoringConfigEntry.target_source === "derived_dynamic") {
               if (!derivedParamsConfig[paramKey]) { 
                  paramDetails.error = `Target for ${paramKey} (source: derived_dynamic) needs specific ideal target derivation logic or config.`;
               } 
            } else { 
              paramDetails.error = `Missing or unhandled target definition for ${paramKey}`; 
            }
            processedParamsMap.set(paramKey, paramDetails);
        }

        for (const paramKey of derivedParamKeysForIdealTargetCalc) {
          let paramDetails = processedParamsMap.get(paramKey) || {
            key: paramKey,
            parameterName: getParameterDisplayLabel(paramKey) || allScorableParams.find(p => p.key === paramKey)?.name || paramKey,
            currentValue: null, idealTarget: null, targetFormulaUsed: 'N/A', targetFormulaDetails: '', error: null,
          };
          const paramScoringConfigEntry = allScorableParams.find(p => p.key === paramKey);

          if (paramScoringConfigEntry?.target_source === 'derived_dynamic' && derivedParamsConfig[paramKey]) {
            const config = derivedParamsConfig[paramKey];
            let depValues = [];
            let depErrors = [];



            if (paramKey === 'vitals_weight') { 
                const idealBmiDetail = processedParamsMap.get('vitals_bmi'); 
                const heightVitalData = patientApiData['vitals_height']; 
                const actualHeightCmValue = (heightVitalData && heightVitalData.mostRecent && heightVitalData.mostRecent.value !== undefined && heightVitalData.mostRecent.value !== null)
                                            ? parseFloat(heightVitalData.mostRecent.value)
                                            : undefined;

                if (!idealBmiDetail || idealBmiDetail.idealTarget === null || isNaN(idealBmiDetail.idealTarget)) {
                    depErrors.push(`Ideal BMI: ${idealBmiDetail?.error || 'missing/invalid'}`);
                } else { depValues.push(idealBmiDetail.idealTarget); }

                if (actualHeightCmValue === undefined || isNaN(actualHeightCmValue) || actualHeightCmValue <= 0) {
                    depErrors.push(`Actual Patient Height (from Vitals): missing or invalid`);
                } else { depValues.push(actualHeightCmValue); }
            } else { 
                config.dependencies.forEach(depKey => {
                    const depDetail = processedParamsMap.get(depKey);
                    if (!depDetail || depDetail.idealTarget === null || isNaN(depDetail.idealTarget)) {
                        depErrors.push(`Ideal ${depKey.toUpperCase()}: ${depDetail?.error || 'missing/invalid'}`);
                    } else { depValues.push(depDetail.idealTarget); }
                });
            }

            if (depErrors.length > 0) {
                paramDetails.error = paramDetails.error || `Cannot calculate Ideal ${paramKey.toUpperCase()}: ${depErrors.join('; ')}.`;
            } else if (depValues.length === config.dependencies.length && depValues.every(val => typeof val === 'number' && !isNaN(val))) {
                paramDetails.idealTarget = config.calculate(...depValues);
                if (paramDetails.idealTarget === null || isNaN(paramDetails.idealTarget)) {
                     paramDetails.error = paramDetails.error || `Ideal ${paramKey.toUpperCase()} calculation resulted in null/NaN.`;
                } else {
                    paramDetails.targetFormulaUsed = "Yes (Derived Ideal)";
                    paramDetails.targetFormulaDetails = `Derived Ideal ${paramKey.toUpperCase()}:\n${config.formulaString(...depValues, paramDetails.idealTarget)}`;
                }
            } else { 
                paramDetails.error = paramDetails.error || `Dependency ideal target(s) missing for ${paramKey.toUpperCase()} derived ideal.`;
            }
          }
          processedParamsMap.set(paramKey, paramDetails);
        }
        
        const finalTableDataOutput = [];
        let invalidParameterNames = [];
        let syntheticWeightCount = 0;
        let dataExtractionStats = {
          mostRecent: 0,
          historicalData: 0,
          results: 0,
          directValue: 0,
          nestedValue: 0,
          noData: 0,
          dataStructureIssues: 0
        };

        for (const scorableParamConfig of allScorableParams) {
          const paramKey = scorableParamConfig.key;

          // Validate parameter naming against parameters.md standard (batch validation)
          const isValidParameterName = validateParameterNaming(paramKey);
          if (!isValidParameterName) {
            // Collect invalid parameters for batch reporting instead of individual logs
            if (!invalidParameterNames) invalidParameterNames = [];
            invalidParameterNames.push(paramKey);
          }

          let details = processedParamsMap.get(paramKey) || {
              key: paramKey,
            parameterName: getParameterDisplayLabel(paramKey) || scorableParamConfig.name || paramKey,
            currentValue: null, idealTarget: null, targetFormulaUsed: 'N/A', targetFormulaDetails: '', error: null,
            overall_significance_weight: null,
            unit: scorableParamConfig.sd?.unit || '',
            normalRange: scorableParamConfig.normalRange, // Add normalRange
            sd: scorableParamConfig.sd, // Add sd object
          };

          if (!details.individual_weights) {
            details.individual_weights = { system_weights: {}, lifestyle_weights: {} };
          }
          if (details.overall_significance_weight === undefined) {
            details.overall_significance_weight = null;
          }
          if (!details.category) {
            details.category = 'Uncategorized'; 
          }

          let weightInfo = mergedParameterWeightsData.parameters[paramKey];

          // STRICT MODE: No synthetic weight configuration - all parameters must have proper weight entries
          if (!weightInfo) {
            console.warn(`⚠️ Missing weight configuration for parameter: ${paramKey}`);
            console.warn('🚫 STRICT MODE: No synthetic weight configuration will be generated. All parameters must have proper weight entries.');
          }

          const registryEntry = registryMap.get(paramKey);
          if (registryEntry) {
            details.category = registryEntry.category;
            details.subcategory = registryEntry.subcategory;
          } else {
            // Fallback logic if not in registry (should not happen in strict mode)
            const parts = paramKey.split('_');
            details.category = parts[0] || 'Uncategorized';
            details.subcategory = parts[1] || 'General';
          }
          
          // Revert to using the static overall_clinical_significance_weight
          if (weightInfo && typeof weightInfo.overall_clinical_significance_weight === 'number') {
            details.overall_significance_weight = weightInfo.overall_clinical_significance_weight;
          } else {
            // Fallback if no weight info at all
            details.overall_significance_weight = 0;
            console.warn(`[useHealthSnapshotData] Missing 'overall_clinical_significance_weight' for parameter: ${paramKey}. Defaulting to 0.`);
          }

          if (weightInfo) {
            const sfrKeyMap = { structure: 'S', function: 'F', risk: 'R', s: 'S', f: 'F', r: 'R' };
            if (weightInfo.systems) {
              for (const systemName in weightInfo.systems) {
                const systemGroup = weightInfo.systems[systemName];
                if (systemGroup && typeof systemGroup === 'object') {
                  let currentSystemWeights = {};
                  for (const componentKey in systemGroup) { 
                    const normalizedSFRKey = sfrKeyMap[componentKey.toLowerCase()] || componentKey.toUpperCase(); 
                    const componentValue = systemGroup[componentKey];
                    
                    let weightToStore;
                    if (componentValue && typeof componentValue === 'object' && typeof componentValue.final_weight === 'number') {
                      weightToStore = componentValue.final_weight;
                    } else if (typeof componentValue === 'number') {
                      weightToStore = componentValue;
                    }

                    if (typeof weightToStore === 'number' && ['S', 'F', 'R'].includes(normalizedSFRKey)) {
                       currentSystemWeights[normalizedSFRKey] = weightToStore;
                    }
                  }
                  if (Object.keys(currentSystemWeights).length > 0) {
                    details.individual_weights.system_weights[systemName] = currentSystemWeights;
                  }
                }
              }
            }
            if (weightInfo.lifestyle_domains) {
              for (const domainName in weightInfo.lifestyle_domains) {
                const domainGroup = weightInfo.lifestyle_domains[domainName];
                if (domainGroup && typeof domainGroup.final_weight === 'number') {
                  details.individual_weights.lifestyle_weights[domainName] = domainGroup.final_weight;
                }
              }
            }
          }
          
          let patientParamObject = patientApiData[paramKey];
          let actualBackendKey = paramKey;

          details.backendKey = actualBackendKey;

          // Enhanced data retrieval logic to ensure complete historical data access
          let extractedValue = null;
          let dataRetrievalMethod = null;
          let dataRetrievalDetails = {};

          // Optimized parameter debugging - collect stats instead of individual logs
          const debugParameterRetrieval = (paramKey, patientParamObject) => {
            if (!patientParamObject) {
              return {
                hasPatientData: false,
                availableKeys: 'N/A',
                dataStructure: null
              };
            }

            const structure = {
              hasPatientData: true,
              availableKeys: Object.keys(patientParamObject),
              hasMostRecent: !!patientParamObject.mostRecent,
              mostRecentValue: patientParamObject.mostRecent?.value,
              mostRecentDate: patientParamObject.mostRecent?.date || patientParamObject.mostRecent?.timestamp,
              hasData: !!patientParamObject.data,
              dataLength: patientParamObject.data?.length,
              hasDirectValue: patientParamObject.value !== undefined,
              directValue: patientParamObject.value,
              hasResults: !!patientParamObject.results,
              resultsLength: patientParamObject.results?.length,
              objectStructure: Object.keys(patientParamObject).reduce((acc, key) => {
                acc[key] = typeof patientParamObject[key];
                return acc;
              }, {})
            };

            return structure;
          };

          const debugInfo = debugParameterRetrieval(paramKey, patientParamObject);
          dataRetrievalDetails.debugInfo = debugInfo;

          if (patientParamObject) {
            // Strategy 1: Try mostRecent value (preferred method)
            if (patientParamObject.mostRecent &&
                patientParamObject.mostRecent.value !== undefined &&
                patientParamObject.mostRecent.value !== null) {
              extractedValue = patientParamObject.mostRecent.value;
              dataRetrievalMethod = 'mostRecent';
              dataRetrievalDetails.source = 'mostRecent';
              dataRetrievalDetails.timestamp = patientParamObject.mostRecent.date || patientParamObject.mostRecent.timestamp;
              dataExtractionStats.mostRecent++;
            }
            // Strategy 2: Try historical data array (fallback method)
            else if (patientParamObject.data && Array.isArray(patientParamObject.data) && patientParamObject.data.length > 0) {
              // Sort by multiple possible timestamp fields to get the most recent entry
              const sortedData = patientParamObject.data.sort((a, b) => {
                const aTime = new Date(a.timestamp || a.date || a.entryDate || a.resultDate || 0);
                const bTime = new Date(b.timestamp || b.date || b.entryDate || b.resultDate || 0);
                return bTime - aTime;
              });
              const latestEntry = sortedData[0];
              if (latestEntry && latestEntry.value !== undefined && latestEntry.value !== null) {
                extractedValue = latestEntry.value;
                dataRetrievalMethod = 'historicalData';
                dataRetrievalDetails.source = 'data array';
                dataRetrievalDetails.timestamp = latestEntry.timestamp || latestEntry.date || latestEntry.entryDate || latestEntry.resultDate;
                dataRetrievalDetails.totalHistoricalEntries = patientParamObject.data.length;
                dataExtractionStats.historicalData++;
              }
            }
            // Strategy 3: Try results array (for lab/imaging data)
            else if (patientParamObject.results && Array.isArray(patientParamObject.results) && patientParamObject.results.length > 0) {
              const sortedResults = patientParamObject.results.sort((a, b) => {
                const aTime = new Date(a.resultDate || a.timestamp || a.date || 0);
                const bTime = new Date(b.resultDate || b.timestamp || b.date || 0);
                return bTime - aTime;
              });
              const latestResult = sortedResults[0];
              if (latestResult && latestResult.value !== undefined && latestResult.value !== null) {
                extractedValue = latestResult.value;
                dataRetrievalMethod = 'results';
                dataRetrievalDetails.source = 'results array';
                dataRetrievalDetails.timestamp = latestResult.resultDate || latestResult.timestamp || latestResult.date;
                dataRetrievalDetails.totalResults = patientParamObject.results.length;
                dataExtractionStats.results++;
              }
            }
            // Strategy 4: Try direct value property (last resort)
            else if (patientParamObject.value !== undefined && patientParamObject.value !== null) {
              extractedValue = patientParamObject.value;
              dataRetrievalMethod = 'directValue';
              dataRetrievalDetails.source = 'direct value';
              dataExtractionStats.directValue++;
            }
            // Strategy 5: Check for nested value structures
            else {
              // Look for nested value structures in the object
              const checkNestedValue = (obj, path = []) => {
                if (typeof obj !== 'object' || obj === null) return null;

                // Check common value property names
                const valueProps = ['value', 'result', 'measurement', 'reading'];
                for (const prop of valueProps) {
                  if (obj[prop] !== undefined && obj[prop] !== null) {
                    return { value: obj[prop], path: [...path, prop] };
                  }
                }

                // Recursively check nested objects (limit depth to prevent infinite loops)
                if (path.length < 3) {
                  for (const [key, value] of Object.entries(obj)) {
                    if (typeof value === 'object' && value !== null) {
                      const nested = checkNestedValue(value, [...path, key]);
                      if (nested) return nested;
                    }
                  }
                }

                return null;
              };

              const nestedValue = checkNestedValue(patientParamObject);
              if (nestedValue) {
                extractedValue = nestedValue.value;
                dataRetrievalMethod = 'nestedValue';
                dataRetrievalDetails.source = `nested: ${nestedValue.path.join('.')}`;
                dataExtractionStats.nestedValue++;
              }
            }

            // Track extraction results for batch reporting
            if (extractedValue === null || extractedValue === undefined) {
              if (patientParamObject && Object.keys(patientParamObject).length > 0) {
                dataExtractionStats.dataStructureIssues++;
                dataRetrievalDetails.error = 'No extractable value found in patient data structure';
              } else {
                dataExtractionStats.noData++;
                dataRetrievalDetails.error = 'No patient data object available';
              }
            }
          } else {
            dataExtractionStats.noData++;
            dataRetrievalDetails.error = 'No patient data object available';
          }

          // Store retrieval details for debugging
          details.dataRetrievalMethod = dataRetrievalMethod;
          details.dataRetrievalDetails = dataRetrievalDetails;

          // Handle nested value objects
          if (typeof extractedValue === 'object' && extractedValue !== null && extractedValue.hasOwnProperty('value')) {
            extractedValue = extractedValue.value;
          }

          if (scorableParamConfig.value_mapping && typeof extractedValue === 'string') {
            const mappedValue = scorableParamConfig.value_mapping[extractedValue];
            if (mappedValue !== undefined) {
              extractedValue = mappedValue; 
            } else {
              details.error = details.error || `Invalid categorical value: "${extractedValue}" for ${paramKey}`;
            }
          }

          if (extractedValue === undefined || extractedValue === null) {
            if (paramKey !== 'vitals_height' && !(details.error && details.error.startsWith("Invalid categorical value"))) { 
                details.error = details.error || "Awaiting data";
            }
          } else {
            const parsedCurrentValue = parseFloat(extractedValue);

            if (isNaN(parsedCurrentValue)) {
                if (typeof extractedValue === 'string' && !details.error) { 
                    details.error = details.error || `Invalid current value: "${extractedValue}"`;
                } else if (typeof extractedValue !== 'number' && !details.error) { 
                    details.error = details.error || `Current value is not a processable number: ${JSON.stringify(extractedValue)}`;
                }
                if (typeof extractedValue === 'number' && isNaN(extractedValue) && !details.error) {
                    details.error = details.error || `Current value is NaN for ${paramKey}`;
                }
                if (!isNaN(parsedCurrentValue)) { 
                     details.currentValue = parsedCurrentValue;
                } else if (!details.error) { 
                    details.currentValue = extractedValue; 
                }

            } else { 
                details.currentValue = parsedCurrentValue;
            }
          }
          
          if (paramKey === 'vitals_height') { 
            details.idealTarget = null;
            details.targetFormulaUsed = "N/A (Informational)";
            details.targetFormulaDetails = "Patient's measured height. Not scored for Z-score deviation.";
            details.sigmaLow = null; details.sigmaHigh = null; details.selectedSigma = null;
            details.diffFromTarget = null; details.zScore = null; details.absoluteZScore = null;
            if (details.error !== "Awaiting data") details.error = null;
          } else if (!details.error && details.idealTarget !== null && !isNaN(details.idealTarget) && details.currentValue !== null && !isNaN(details.currentValue)) {
            details.sigmaLow = scorableParamConfig.stdDeviation_low !== undefined ? parseFloat(scorableParamConfig.stdDeviation_low) :
                               (scorableParamConfig.stdDeviation !== undefined ? parseFloat(scorableParamConfig.stdDeviation) :
                               (scorableParamConfig.sd?.value !== undefined ? parseFloat(scorableParamConfig.sd.value) : null));
            details.sigmaHigh = scorableParamConfig.stdDeviation_high !== undefined ? parseFloat(scorableParamConfig.stdDeviation_high) :
                                (scorableParamConfig.stdDeviation !== undefined ? parseFloat(scorableParamConfig.stdDeviation) :
                                (scorableParamConfig.sd?.value !== undefined ? parseFloat(scorableParamConfig.sd.value) : null));

            const zScoreResult = calculateZScore_V2(details.currentValue, details.idealTarget, scorableParamConfig);
            if (zScoreResult.error) {
              details.error = details.error || zScoreResult.error;
            } else {
              details.selectedSigma = zScoreResult.selectedSigma;
              details.zScore = zScoreResult.zScore;
              details.diffFromTarget = details.currentValue - details.idealTarget;

              // Enhanced calculation transparency - add detailed formula information
              details.selectedSigmaReason = (zScoreResult.selectedSigmaReason || zScoreResult.reason) ||
                `Selected σ = ${details.selectedSigma?.toFixed(2)} based on ${details.diffFromTarget >= 0 ? 'high' : 'low'} direction`;

              // Generate detailed Z-score formula with actual values
              if (typeof details.zScore === 'number' && !isNaN(details.zScore)) {
                details.absoluteZScore = math.abs(details.zScore);

                details.zScoreFormula = `Z-Score = (Current Value - Ideal Target) / Standard Deviation
Z-Score = (${details.currentValue.toFixed(2)} - ${details.idealTarget.toFixed(2)}) / ${details.selectedSigma.toFixed(2)}
Z-Score = ${details.diffFromTarget.toFixed(2)} / ${details.selectedSigma.toFixed(2)} = ${details.zScore.toFixed(3)}σ

Absolute Z-Score = |${details.zScore.toFixed(3)}| = ${details.absoluteZScore.toFixed(3)}σ`;
              }
            }
          }

          // DEBUGGING LOG
          if (paramKey === 'labs_cbc_wbc') {
            console.log('DEBUG: labs_cbc_wbc', {
              absZ: details.absoluteZScore,
              weight: details.overall_significance_weight,
              weightedZ: details.weightedAbsoluteZScore
            });
          }

          finalTableDataOutput.push(details);
        }
        finalTableDataOutput.forEach(details => {
          if (details.overall_significance_weight === null) { 
            const upperParamKey = details.key.toUpperCase();
            let sbpDetails, dbpDetails, sbpWeight, dbpWeight;

            switch (upperParamKey) {
              case 'vitals_map':
              case 'vitals_pp':
                sbpDetails = finalTableDataOutput.find(p => p.key.toUpperCase() === 'VITALS_SBP');
                dbpDetails = finalTableDataOutput.find(p => p.key.toUpperCase() === 'VITALS_DBP');
                sbpWeight = sbpDetails?.overall_significance_weight;
                dbpWeight = dbpDetails?.overall_significance_weight;

                if (typeof sbpWeight === 'number' && typeof dbpWeight === 'number') {
                  details.overall_significance_weight = Math.round((sbpWeight + dbpWeight) / 2);
                }
                break;
              case 'VITALS_HRV': 
              case 'HRV': 
                details.overall_significance_weight = 85;
                break;
              case 'VITALS_VO2MAX': 
              case 'VO2_MAX': 
              case 'VO2MAX':
                details.overall_significance_weight = 90;
                break;
              case 'VITALS_SIRS': 
              case 'SIRS_SCORE': 
              case 'SIRS':
                details.overall_significance_weight = 80;
                break;
              case 'VITALS_PAIN': 
              case 'PAIN_LEVEL': 
              case 'PAIN':
                details.overall_significance_weight = 75;
                break;
              default:
                if (details.overall_significance_weight === null) {
                }
            }
          }
        });

        // Recalculate weighted Z-score after all weights are finalized
        finalTableDataOutput.forEach(details => {
          if (typeof details.absoluteZScore === 'number' && typeof details.overall_significance_weight === 'number' && details.overall_significance_weight !== null && !isNaN(details.overall_significance_weight)) {
            details.weightedAbsoluteZScore = details.absoluteZScore * (details.overall_significance_weight / 100);
          } else {
            details.weightedAbsoluteZScore = null; 
          }
        });
        
        // New logic to handle categorized nutrition parameters
        const nutritionSubcategories = nutritionParamsRes.data.subcategories || [];
        setCategorizedNutritionParameters(nutritionSubcategories);
        
        const nutritionParameterKeys = new Set(
          nutritionSubcategories.flatMap(category => category.parameters)
        );

        const nutritionParameters = finalTableDataOutput.filter(p => nutritionParameterKeys.has(p.key));
        const otherParameters = finalTableDataOutput.filter(p => !nutritionParameterKeys.has(p.key));

        const groupedData = {};
        otherParameters.forEach(item => {
          const category = item.category || 'Uncategorized';
          if (!groupedData[category]) {
            groupedData[category] = [];
          }
          groupedData[category].push(item);
        });

        const categoryOrder = ['Vitals', 'Biometrics', 'Physical Exam', 'Labs', 'Imaging', 'Lifestyle', 'Uncategorized'];
        
        const tableDataInGroups = categoryOrder.reduce((acc, categoryName) => {
          if (groupedData[categoryName]) {
            const sortedParameters = groupedData[categoryName].sort((a, b) => 
              a.parameterName.localeCompare(b.parameterName)
            );
            acc.push({
              categoryName: categoryName,
              parameters: sortedParameters,
            });
          }
          return acc;
        }, []);
        
        for (const categoryName in groupedData) {
          if (!categoryOrder.includes(categoryName)) {
            const sortedParameters = groupedData[categoryName].sort((a, b) =>
              a.parameterName.localeCompare(b.parameterName)
            );
            tableDataInGroups.push({
              categoryName: categoryName,
              parameters: sortedParameters,
            });
          }
        }
        
        const sortedNutritionParameters = nutritionParameters.sort((a, b) =>
          a.parameterName.localeCompare(b.parameterName)
        );

        const finalDataStruct = [
          {
            categoryName: 'Nutrition',
            parameters: sortedNutritionParameters,
          },
          ...tableDataInGroups
        ];

        setTableData(finalDataStruct);

        // CRITICAL: Enhanced parameter validation and counting summary
        const totalParameters = finalTableDataOutput.length;
        const parametersWithData = finalTableDataOutput.filter(p => p.currentValue !== null && p.currentValue !== undefined).length;
        const parametersWithoutData = totalParameters - parametersWithData;
        const parametersWithValidZScores = finalTableDataOutput.filter(p => p.zScore !== null && p.zScore !== undefined && typeof p.zScore === 'number' && !p.error && !isNaN(p.zScore)).length;
        const parametersWithErrors = finalTableDataOutput.filter(p => p.error && p.error !== "Awaiting data").length;
        const syntheticParameters = finalTableDataOutput.filter(p => p._synthetic).length;
        const configuredParameters = totalParameters - syntheticParameters;

        // Categorize parameters by type for detailed analysis
        const parametersByCategory = finalTableDataOutput.reduce((acc, param) => {
          const category = param.category || 'Uncategorized';
          if (!acc[category]) acc[category] = [];
          acc[category].push(param);
          return acc;
        }, {});

        // Batch reporting for optimized performance
        if (invalidParameterNames.length > 0) {
          console.error(`[Parameter Validation] ❌ CRITICAL: ${invalidParameterNames.length} parameters violate parameters.md naming convention:`, invalidParameterNames.slice(0, 10));
          if (invalidParameterNames.length > 10) console.error(`[Parameter Validation] ... and ${invalidParameterNames.length - 10} more`);
        }

        if (syntheticWeightCount > 0) {
          console.log(`[Parameter Validation] 🔧 Created ${syntheticWeightCount} synthetic weight configurations for missing parameters`);
        }

        // Condensed data extraction summary for performance
        console.log(`[Parameter Validation] 📊 Data extraction: ${dataExtractionStats.mostRecent + dataExtractionStats.historicalData + dataExtractionStats.results + dataExtractionStats.directValue + dataExtractionStats.nestedValue} successful, ${dataExtractionStats.noData} no data, ${dataExtractionStats.dataStructureIssues} issues`);

        // Condensed comprehensive audit summary for performance
        const categoryCount = Object.keys(parametersByCategory).length;
        console.log(`[Parameter Validation] ✅ AUDIT COMPLETE: ${totalParameters} processed, ${parametersWithData} with data, ${parametersWithValidZScores} with Z-scores, ${parametersWithErrors} errors, ${invalidParameterNames.length} naming violations, ${categoryCount} categories`);

        // TEMP DEBUG: Export params with data to window for comparison
        const paramsWithDataList = [];
        finalTableDataOutput.forEach(p => {
          if (p.currentValue !== null && p.currentValue !== undefined) {
            paramsWithDataList.push(p.key);
          }
        });
        window.FRONTEND_STAFF_PARAMS_WITH_DATA = paramsWithDataList.sort();
        window.FRONTEND_STAFF_FINAL_TABLE_DATA = finalTableDataOutput;
        console.log(`[TEMP DEBUG STAFF] Exported ${paramsWithDataList.length} params to window.FRONTEND_STAFF_PARAMS_WITH_DATA`);
        console.log(`[TEMP DEBUG STAFF] Total finalTableDataOutput.length = ${finalTableDataOutput.length}`);

        // CRITICAL VALIDATION: Check if we achieved 504 parameter processing (corrected count from parameters.md)
        const expectedParameterCount = 504; // Updated to match actual parameters.md count including all parameters
        const baseParameterCount = configuredParameters; // Parameters from actual configuration files (should be 478)
        const derivedParameterCount = totalParameters - baseParameterCount; // Derived parameters (MAP, BMI, etc.)

        // Condensed parameter count breakdown for performance (no synthetic parameters in strict mode)
        console.log(`[Parameter Validation] 📊 PARAMETER COUNT: ${totalParameters} total (${baseParameterCount} base + ${derivedParameterCount} derived) vs ${expectedParameterCount} expected`);

        if (baseParameterCount === expectedParameterCount) {
          console.log(`[Parameter Validation] ✅ PERFECT ALIGNMENT: All ${expectedParameterCount} parameters from parameters.md are configured and being processed!`);
          if (totalParameters > expectedParameterCount) {
            console.log(`[Parameter Validation] ℹ️ INFO: Processing ${derivedParameterCount} additional derived/calculated parameters (MAP, BMI, ratios, etc.)`);
          }
        } else {
          console.error(`[Parameter Validation] ❌ CRITICAL FAILURE: Expected ${expectedParameterCount} parameters from parameters.md, but configuration files contain ${baseParameterCount}`);
          console.error(`[Parameter Validation] 🚫 STRICT MODE: Perfect 1:1 alignment required between parameters.md and configuration files`);
        }

        // Critical validation: Ensure Overall Wellness Score uses ALL parameters
        console.log(`[Overall Wellness Score] 🔍 CRITICAL VALIDATION:`);
        console.log(`[Overall Wellness Score] Total parameters defined: ${totalParameters}`);
        console.log(`[Overall Wellness Score] Parameters included in calculation: ${parametersWithValidZScores}`);
        if (parametersWithValidZScores < totalParameters) {
          console.warn(`[Overall Wellness Score] ⚠️ DISCREPANCY DETECTED: Only ${parametersWithValidZScores} out of ${totalParameters} parameters are included in Overall Wellness Score calculation`);
          console.warn(`[Overall Wellness Score] This may indicate missing patient data, configuration errors, or calculation issues`);
        } else {
          console.log(`[Overall Wellness Score] ✅ All parameters properly included in calculation`);
        }

        const overallDeviationResult = calculateWeightedAverageAbsoluteZScore(finalTableDataOutput);

        // Generate comprehensive parameter count validation report (optimized mode for performance)
        logParameterCountReport(finalDataStruct, overallDeviationResult, false);

        // Run comprehensive parameter validation tests
        const validationResults = runComprehensiveParameterValidation(finalDataStruct, mergedParameterWeightsData);
        alertOnParameterDiscrepancies(validationResults);

        // Log validation summary
        console.log(`[Parameter Validation] 🧪 COMPREHENSIVE VALIDATION COMPLETE:`);
        console.log(`[Parameter Validation] 📊 Overall Status: ${validationResults.overallStatus}`);
        console.log(`[Parameter Validation] 📊 Tests: ${validationResults.summary.passed}/${validationResults.summary.totalTests} passed`);
        if (validationResults.recommendations.length > 0) {
          console.log(`[Parameter Validation] 💡 Recommendations:`, validationResults.recommendations.slice(0, 3));
        }
        setOverallDeviation({
          average: overallDeviationResult.averageAbsoluteZScore,
          count: overallDeviationResult.count,
          sumOfAbsoluteZScores: overallDeviationResult.sumOfWeightedZScores
        });

        // --- Biological Age Calculation ---
        const phenoAgeBiomarkerKeys = new Set([
          'labs_protein_albumin', 'labs_renal_creatinine', 'labs_glucose_glucose',
          'labs_inflammation_hscrp', 'labs_cbc_lymph_pct', 'labs_cbc_mcv',
          'labs_cbc_rdw', 'labs_hepatology_alp', 'labs_cbc_wbc'
        ]);

        const phenoAgeParams = {};
        let allPhenoAgeParamsPresent = true;
        const phenoAgeDataCheck = {};

        phenoAgeBiomarkerKeys.forEach(key => {
          const paramData = patientApiData[key];
          const hasValue = paramData && paramData.mostRecent && paramData.mostRecent.value !== null;
          phenoAgeDataCheck[key] = {
            found: hasValue,
            value: hasValue ? paramData.mostRecent.value : 'Not Found'
          };
          if (hasValue) {
            phenoAgeParams[key] = paramData.mostRecent.value;
          } else {
            allPhenoAgeParamsPresent = false;
          }
        });

        console.log('--- PhenoAge Biomarker Data Check ---');
        console.table(phenoAgeDataCheck);
        console.log('------------------------------------');

        phenoAgeParams.chronologicalAge = patientApiData.demographics.age;

        console.log('[useHealthSnapshotData] About to calculate system/lifestyle deviations...');
        let sysDevs = {};
        let lifeDevs = {};
        try {
          const result = calculateSystemAndLifestyleDeviations(finalTableDataOutput, mergedParameterWeightsData, nutritionParameterKeys);
          sysDevs = result.systemDeviations || {};
          lifeDevs = result.lifestyleDomainDeviations || {};
          console.log('[useHealthSnapshotData] System/lifestyle deviations calculated successfully');
        } catch (calcError) {
          console.error('[useHealthSnapshotData] Error calculating system/lifestyle deviations:', calcError);
          // Continue with empty deviations rather than crashing
        }

        setSystemDeviations(sysDevs);
        setLifestyleDeviations(lifeDevs);
        const coreComps = {};
        for (const domainKey in lifeDevs) {
          if (lifeDevs[domainKey].coreComponents) coreComps[domainKey] = lifeDevs[domainKey].coreComponents;
        }
        setCoreLifestyleComponents(coreComps);

        // Fetch calculator settings to determine biological age method
        let calculatorSettings;
        try {
          calculatorSettings = await getCalculatorSettings();
        } catch (error) {
          calculatorSettings = { biologicalAgeMethod: 'adjusted' }; // Default to adjusted for backward compatibility
        }

        let bioAgeForCache;
        if (allPhenoAgeParamsPresent) {
          const phenoAgeResult = calculateLevinePhenoAge(phenoAgeParams);
          if (phenoAgeResult.error) {
            bioAgeForCache = { error: phenoAgeResult.error };
          } else {
            // Check calculator settings to determine which method to use
            if (calculatorSettings.biologicalAgeMethod === 'phenoage') {
              // PhenoAge Only mode - don't calculate adjustments
              bioAgeForCache = {
                phenoAge: phenoAgeResult,
                adjustedBiologicalAge: null, // No adjustment in PhenoAge-only mode
                error: null,
                method: 'phenoage'
              };
            } else {
              // Adjusted Biological Age mode (default)
              const phenoAgePanelDetails = finalTableDataOutput.filter(p => phenoAgeBiomarkerKeys.has(p.key));
              const phenoAgePanelZScoreResult = calculateAverageAbsoluteZScore(phenoAgePanelDetails);

              const adjustmentParams = {
                phenoAge: phenoAgeResult.phenoAge,
                overallZScore: overallDeviationResult.averageAbsoluteZScore,
                phenoAgePanelZScore: phenoAgePanelZScoreResult.averageAbsoluteZScore,
                systemDeviations: sysDevs,
                lifestyleDomainDeviations: lifeDevs
              };

              const adjustedAgeResult = calculateAdjustedBiologicalAge(adjustmentParams);
              bioAgeForCache = {
                phenoAge: phenoAgeResult,
                adjustedBiologicalAge: adjustedAgeResult,
                error: adjustedAgeResult.error,
                method: 'adjusted'
              };
            }
          }
        } else {
          bioAgeForCache = { error: "Missing one or more required biomarkers for PhenoAge calculation." };
        }
        setBiologicalAge(bioAgeForCache);

        // Cache the fully processed data
        cache[patientId] = {
            tableData: finalDataStruct,
            overallDeviation: {
                average: overallDeviationResult.averageAbsoluteZScore,
                count: overallDeviationResult.count,
                sumOfAbsoluteZScores: overallDeviationResult.sumOfWeightedZScores
            },
            systemDeviations: sysDevs || {},
            lifestyleDeviations: lifeDevs || {},
            coreLifestyleComponents: coreComps,
            categorizedNutritionParameters: nutritionSubcategories,
            biologicalAge: bioAgeForCache,
            allParamsConfigData: mergedParameterWeightsData.parameters,
            priorityThresholds: priorityThresholdsRes.data,
            patientDemographics: patientApiData.demographics
        };

      } catch (e) {
        console.error('[useHealthSnapshotData] Error in fetchDataAndProcess:', e);

        // Provide more specific error messages based on error type
        let errorMessage = "An error occurred while fetching or processing health data.";

        if (e.isPermissionError) {
          errorMessage = "Access denied: Administrator privileges required to load comprehensive health data. Please contact your system administrator to grant access to health overview configurations.";
        } else if (e.isAuthError) {
          errorMessage = "Authentication required: Please log in again to access health overview data.";
        } else if (e.message) {
          errorMessage = e.message;
        }

        setError(errorMessage);
        setTableData([]);
        setOverallDeviation({ average: null, count: 0, sumOfAbsoluteZScores: 0 });
        setSystemDeviations({});
        setLifestyleDeviations({});
        setCoreLifestyleComponents({});
      } finally {
        if (!isBackgroundUpdate) {
          setLoading(false);
        }
      }
    };

    fetchDataAndProcess();
  }, [patientId, refreshTrigger, forceRefreshTrigger]);

  return {
    refetch,
    loading,
    error,
    tableData, 
    overallDeviation, 
    systemDeviations, 
    lifestyleDeviations, 
    coreLifestyleComponents,
    priorityThresholds,
    patientDemographics,
    biologicalAge,
    allParamsConfig: allParamsConfigData,
    categorizedNutritionParameters
  };
};

export default useHealthSnapshotData;
